import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import parse from 'html-react-parser';
import {Trans, useTranslation} from 'react-i18next';

import BasePageProps from '../../view2D/BasePageProps';
import BaseSection from '../../view2D/BaseSection';

import { useNavigationContext } from '../../../context/NavigationContext';
import { useAudioContext } from '../../../context/AudioContext';
import { SectionIDs } from '../../../data/Sections';

import thumbnailVector from '../../../assets/images/thumbnails/Vector-representation.jpg';
import thumbnailSphere from '../../../assets/images/thumbnails/SphereEmbedding.jpg';
import thumbnailSubspace from '../../../assets/images/thumbnails/SphereSubspaces.jpg';
import thumbnailTopology from '../../../assets/images/thumbnails/TopologicalRelations.jpg';
import thumbnailBehaviour from '../../../assets/images/thumbnails/Dynamicbehaviour.jpg';
import { ReactComponent as ArrowRightIcon } from '../../../assets/images/icons/arrow-right.svg';

import styles from './styles.module.scss';

const Thumbnails = [
  {position: 17, image: thumbnailVector},
  {position: 34,   image: thumbnailSphere},
  {position: 50,  image: thumbnailSubspace},
  {position: 66,  image: thumbnailTopology},
  {position: 83,  image: thumbnailBehaviour},
];

const Subtitles = [
  {position: {start: 4, end: 9}, text: 'thejoke.1'},
  {position: {start: 9, end: 15}, text: 'thejoke.2'},
  {position: {start: 15, end: 22}, text: 'thejoke.3'},
  {position: {start: 22, end: 27}, text: 'thejoke.4'},
  {position: {start: 27, end: 35}, text: 'thejoke.5'},
  {position: {start: 35, end: 46}, text: 'thejoke.6'},
  {position: {start: 47, end: 53}, text: 'thejoke.7'},
  {position: {start: 53, end: 58}, text: 'thejoke.8'},
  {position: {start: 58, end: 64}, text: 'thejoke.9'},
  {position: {start: 64, end: 71}, text: 'thejoke.10'},
  {position: {start: 71, end: 80}, text: 'thejoke.11'},
  {position: {start: 80, end: 88}, text: 'thejoke.12'},
  {position: {start: 88, end: 91}, text: 'thejoke.13'},
  {position: {start: 91, end: 100}, text: 'thejoke.14'},
];

interface ITheJoke {
  video?: HTMLVideoElement
}

function TheJoke({ ...props }: BasePageProps & ITheJoke): JSX.Element
{
  const { playAudio } = useAudioContext();
  const { moveToTheSection, activeSectionID } = useNavigationContext();
  const { t } = useTranslation('ai4Jokes');
  const { muteBGM } = useAudioContext();

  const [progress, setProgress] = useState(0);

  const onSkipClicked = useCallback(
    () => {
      moveToTheSection(SectionIDs.Science);
      playAudio('audioSkipReplay');
    },
    [moveToTheSection, playAudio],
  );

  useEffect(()=>{
    const { video } = props;
    if(!video) return;

    const onProgress = () => {
      if(video) setProgress((video.currentTime / video.duration ) * 100);
    };

    video.addEventListener('timeupdate', onProgress);

    return () =>{
      video.removeEventListener('timeupdate', onProgress);
    };
  }, [props.video]);

  useEffect(() => {
    muteBGM(activeSectionID === props.sectionID);
  }, [activeSectionID, muteBGM, props.sectionID]);

  useEffect(() => {
    if(progress >= 100)
      onSkipClicked();
  }, [progress, onSkipClicked]);

  const currentSubtitle = useMemo(() => Subtitles.find(s => s.position.start <= progress && s.position.end >= progress)?.text ?? '', [progress]);

  return (
    <BaseSection {...props} style={{ position: 'fixed', zIndex: 3, height: '100%' }}>
      <div className={styles.container}>
        <section>
          <div className={styles.progressBar} style={{width: progress + '%'}}></div>
          <div className={styles.progressBarBg}>
            {Thumbnails.map((p, i) => {
              return p.position > progress ? <div key={i}></div> :
                <div key={i} className={styles.thumbnail} style={{left: p.position + '%'}}>
                  <div>
                    <img src={p.image} alt='snapshots from the video' />
                    <div></div>
                  </div>
                  <div className={styles.trail}></div>
                </div>;
            })}
          </div>

        </section>
        <footer className={styles.footer}>
          <div className={styles.subtitles}>
            <span>
              {currentSubtitle && <Trans>{parse(t(currentSubtitle))}</Trans>}
            </span>
          </div>
          <button className={styles.skipButton} onClick={onSkipClicked}>
            {t('thejoke.buttons.skip')}
            <ArrowRightIcon />
          </button>
        </footer>
      </div>
    </BaseSection>
  );
}

export default React.memo(TheJoke);
